/* import __COLOCATED_TEMPLATE__ from './suggestion-implementation-item.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */

import Component from '@glimmer/component';
// @ts-expect-error
import { sanitizeHtml } from '@intercom/pulse/lib/sanitize';
import { type PulseAccordionSection } from 'glint/pulse';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';

interface Signature {
  Args: {
    section: PulseAccordionSection;
    sectionId: string;
    isOpen: boolean;
    reasoning: string | null;
    confidenceSummary: string;
  };
}

export default class SuggestionImplementationItem extends Component<Signature> {
  @service declare intl: IntlService;

  get effort() {
    return this.resolveEffortLabel;
  }

  get formattedReasoning() {
    if (!this.args.reasoning) {
      return '';
    }
    return sanitizeHtml(this.args.reasoning);
  }

  get resolveEffortLabel(): { effortLevel: string; color: 'green' | 'yellow' | 'gray' | 'red' } {
    if (!this.args.confidenceSummary) {
      return {
        effortLevel: this.intl.t('automation.analyze.task-suggestions.effort-score.unknown'),
        color: 'yellow',
      };
    }

    let effortColors: Record<string, 'green' | 'yellow' | 'gray' | 'red'> = {
      High: 'red',
      Medium: 'yellow',
      Low: 'green',
    };

    let efforts = this.args.confidenceSummary
      .split(', ')
      .map((effort) => {
        let [effortLevelPart, countPart] = effort.split(': ');
        let effortLevel = effortLevelPart?.split(' ')[0];
        let numericCount = 0;

        if (countPart) {
          let [rawCount] = countPart.split('/');
          let parsed = parseInt(rawCount, 10);

          if (!Number.isNaN(parsed)) {
            numericCount = parsed;
          }
        }

        return {
          // eslint-disable-next-line @intercom/intercom/no-bare-strings
          effortLevel: effortLevel ?? 'Unknown',
          count: numericCount,
        };
      })
      .sort((a, b) => b.count - a.count);

    // eslint-disable-next-line @intercom/intercom/no-bare-strings
    let topEffort = efforts[0]?.effortLevel ?? 'Unknown';

    return {
      effortLevel: topEffort,
      color: effortColors[topEffort] ?? 'green',
    };
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::OptimizeSuggestions::Suggestions::SuggestionAccordionItems::SuggestionImplementationItem': typeof SuggestionImplementationItem;
    'reporting/automation/ai-insights/optimize-suggestions/suggestions/suggestion-accordion-items/suggestion-implementation-item': typeof SuggestionImplementationItem;
  }
}
